import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './Home.css';
import './Jobs.css'; // Import Jobs.css to use the same question card styles

const Home = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState('');
  const [hasResume, setHasResume] = useState(false);
  const [hasJobDescription, setHasJobDescription] = useState(false);
  const [lastAnswer, setLastAnswer] = useState(null);
  const [handpickedQuestions, setHandpickedQuestions] = useState([]);
  const [hasAnyAnswer, setHasAnyAnswer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const fileInputRef = useRef(null);
  const [jobDescriptionUrl, setJobDescriptionUrl] = useState('');

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUser(user);
        await fetchUserName(user.id);
        await checkResume(user.id);
        await checkJobDescription(user.id);
        await fetchLastAnswer(user.id);
        await fetchHandpickedQuestions(user.id);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserName = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('name')
        .eq('id', userId)
        .single();

      if (error) throw error;
      
      if (data && data.name) {
        setUserName(data.name);
      }
    } catch (error) {
      console.error('Error fetching user name:', error);
    }
  };

  const checkResume = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('resumes')
        .select('id')
        .eq('user_id', userId)
        .eq('status', 'active')
        .limit(1);

      if (error) throw error;
      
      setHasResume(data && data.length > 0);
    } catch (error) {
      console.error('Error checking resume:', error);
    }
  };

  const checkJobDescription = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('job_descriptions')
        .select('id')
        .eq('user_id', userId)
        .limit(1);

      if (error) throw error;
      
      setHasJobDescription(data && data.length > 0);
    } catch (error) {
      console.error('Error checking job description:', error);
    }
  };

  const fetchLastAnswer = async (userId) => {
    try {
      const { data: answers, error } = await supabase
        .from('interview_lab_answers')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (!answers || answers.length === 0) {
        setLastAnswer(null);
        return;
      }

      const latestAnswer = answers[0];
      setHasAnyAnswer(true);

      // Fetch the related question to get job (source) info
      const { data: question, error: questionError } = await supabase
        .from('interview_lab_questions')
        .select('id, question, source_id, source_type')
        .eq('id', latestAnswer.question_id)
        .single();

      if (questionError) throw questionError;

      // Only show card for job-based questions
      if (question.source_type !== 'job_description') {
        setLastAnswer(null);
        setHasAnyAnswer(false);
        return;
      }

      setLastAnswer({
        answerId: latestAnswer.id,
        questionId: latestAnswer.question_id,
        overallScore: latestAnswer.overall_score,
        jobId: question.source_id,
        questionText: question.question,
      });
    } catch (error) {
      console.error('Error fetching last answer:', error);
      setLastAnswer(null);
      setHasAnyAnswer(false);
    }
  };

  const fetchHandpickedQuestions = async (userId) => {
    try {
      // Check if the user has answered at least one question
      const { data: answers, error: answersError } = await supabase
        .from('interview_lab_answers')
        .select('id')
        .eq('user_id', userId)
        .limit(1);

      if (answersError) throw answersError;

      const hasAnswer = !!(answers && answers.length > 0);
      setHasAnyAnswer(hasAnswer);

      if (!hasAnswer) {
        setHandpickedQuestions([]);
        return;
      }

      // Fetch all interview questions across jobs for this user
      const { data: questionsData, error: questionsError } = await supabase
        .from('interview_lab_questions')
        .select('*')
        .eq('user_id', userId)
        .eq('source_type', 'job_description');

      if (questionsError) throw questionsError;

      const allQuestions = questionsData || [];

      if (allQuestions.length === 0) {
        setHandpickedQuestions([]);
        return;
      }

      // Get unique job description IDs
      const jobDescriptionIds = [...new Set(allQuestions.map(q => q.source_id))];

      // Fetch job descriptions to get company names
      let jobDescriptionsMap = {};
      if (jobDescriptionIds.length > 0) {
        const { data: jobDescData, error: jobDescError } = await supabase
          .from('job_descriptions')
          .select('id, company_name')
          .in('id', jobDescriptionIds);

        if (!jobDescError && jobDescData) {
          jobDescData.forEach(jd => {
            jobDescriptionsMap[jd.id] = jd;
          });
        }
      }

      // Attach company name to each question
      const questionsWithCompany = allQuestions.map(q => ({
        ...q,
        company_name: jobDescriptionsMap[q.source_id]?.company_name || 'Unknown Company',
      }));

      // Shuffle and pick up to 5 random questions
      const shuffled = [...questionsWithCompany].sort(() => 0.5 - Math.random());
      setHandpickedQuestions(shuffled.slice(0, 5));
    } catch (err) {
      console.error('Error fetching handpicked questions:', err);
      setHandpickedQuestions([]);
      setHasAnyAnswer(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return '#28a745';
      case 'medium': return '#ffc107';
      case 'hard': return '#dc3545';
      default: return '#666';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (PDF, DOC, DOCX)
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Generate unique file path
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      // Upload file to storage bucket
      const { error: uploadError } = await supabase.storage
        .from('resume')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Set all previous resumes to inactive
      const { error: updateError } = await supabase
        .from('resumes')
        .update({ status: 'inactive' })
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (updateError) throw updateError;

      // Create new resume record with active status
      const { data: insertedResume, error: insertError } = await supabase
        .from('resumes')
        .insert([
          {
            user_id: user.id,
            file_path: filePath,
            file_name: file.name,
            status: 'active'
          }
        ])
        .select()
        .single();

      if (insertError) throw insertError;

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      setUploading(false);
      setAnalyzing(true);

      // Call the edge function to analyze the resume
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          throw new Error('You must be logged in to analyze resume');
        }

        const response = await fetch(
          `https://saqouhdsoiavpwknumaw.supabase.co/functions/v1/analyze-resume`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ resume_id: insertedResume.id }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to analyze resume');
        }

        const analyzedData = await response.json();
        console.log('Resume analyzed successfully:', analyzedData);

        // Refresh resume status
        await checkResume(user.id);
        
        // Show success message
        setSuccessMessage('Resume uploaded and analyzed successfully!');
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
        
      } catch (analyzeError) {
        console.error('Error analyzing resume:', analyzeError);
        // Show partial success - resume uploaded but analysis failed
        setSuccessMessage('Resume uploaded successfully! Analysis will be processed shortly.');
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
      } finally {
        setAnalyzing(false);
      }

    } catch (error) {
      console.error('Error uploading resume:', error);
      setError(error.message || 'Failed to upload resume');
      setUploading(false);
      setAnalyzing(false);
    }
  };

  const handleJobDescriptionSubmit = async () => {
    if (!jobDescriptionUrl.trim()) {
      setError('Please enter a job description URL');
      return;
    }

    // Validate URL format
    try {
      new URL(jobDescriptionUrl);
    } catch (e) {
      setError('Please enter a valid URL');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Call Supabase Edge Function to extract job description
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('You must be logged in to perform this action');
      }

      const response = await fetch(
        `https://saqouhdsoiavpwknumaw.supabase.co/functions/v1/extract-job-description`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ url: jobDescriptionUrl }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to extract job description');
      }

      const jobData = await response.json();

      // Edge Function now handles insertion, so just refresh and show success
      console.log('Job description processed:', {
        company: jobData.company_name,
        title: jobData.job_title,
        questions_generated: jobData.questions_generated
      });

      // Refresh job description status
      await checkJobDescription(user.id);
      
      // Reset URL input
      setJobDescriptionUrl('');
      
      // Show success message
      setSuccessMessage(
        `Job description added successfully! ${jobData.questions_generated || 0} interview questions generated.`
      );
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);

    } catch (error) {
      console.error('Error processing job description:', error);
      setError(error.message || 'Failed to process job description');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="home-page">
        <div className="home-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <div className="home-container">
        <h1>Welcome {userName || 'back'},</h1>
        <p className="home-subtitle">Let's get started with your interview preparation</p>

        <div className="home-content">
          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}
          
          {(() => {
            // Calculate which steps are visible and assign dynamic step numbers
            let stepNumber = 0;
            const steps = [];

            // Step 1: Upload CV (only show if not done)
            if (!hasResume) {
              stepNumber++;
              steps.push(
                <div key="upload-cv" className="step-container">
                  <span className="step-number">{stepNumber}</span>
                  <div className="step-content">
                    <h2>Upload your CV</h2>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <button 
                      className="upload-button" 
                      onClick={handleUploadClick}
                      disabled={uploading || analyzing}
                    >
                      {uploading ? 'Uploading...' : analyzing ? 'Analyzing...' : 'Upload CV'}
                    </button>
                  </div>
                </div>
              );
            }

            // Step 2: Upload Job Description (only show if resume is done but job description is not)
            if (hasResume && !hasJobDescription) {
              stepNumber++;
              steps.push(
                <div key="upload-job" className="step-container">
                  <span className="step-number">{stepNumber}</span>
                  <div className="step-content">
                    <h2>Upload your first job description</h2>
                    <button
                      className="upload-button"
                      onClick={() => navigate('/jobs?addJob=1')}
                      disabled={uploading}
                    >
                      Upload
                    </button>
                  </div>
                </div>
              );
            }

            // Step 3: Answer Questions (only show if both are done AND user hasn't answered any questions yet)
            if (hasResume && hasJobDescription && !lastAnswer) {
              stepNumber++;
              steps.push(
                <div key="answer-questions" className="step-container">
                  <span className="step-number">{stepNumber}</span>
                  <div className="step-content">
                    <h2>Answer your first questions</h2>
                    <button
                      className="upload-button play-button"
                      onClick={() => navigate('/jobs?openLabForFirstJob=1')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 5V19L19 12L8 5Z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Start
                    </button>
                  </div>
                </div>
              );
            }

            // Step 4: Review last answer feedback (only show if user has answered at least one question)
            if (lastAnswer) {
              stepNumber++;
              steps.push(
                <div key="review-feedback" className="step-container">
                  <span className="step-number">{stepNumber}</span>
                  <div className="step-content">
                    <h2>Review last answer feedback</h2>
                    <button
                      className="upload-button"
                      onClick={() =>
                        navigate(
                          `/jobs?feedbackQuestionId=${lastAnswer.questionId}&jobId=${lastAnswer.jobId}`
                        )
                      }
                    >
                      Review feedback
                    </button>
                  </div>
                </div>
              );
            }

            return steps;
          })()}

          {/* Hand picked questions (for users who have answered at least one question) */}
          {hasAnyAnswer && handpickedQuestions.length > 0 && (
            <div className="home-handpicked-section">
              <h2 className="home-handpicked-title">Hand picked questions</h2>
              <div className="home-handpicked-list">
                {handpickedQuestions.map((q, index) => (
                  <div key={q.id} className="question-card">
                    <div className="question-header">
                      <div className="question-number">Q{index + 1}</div>
                      <div className="question-meta">
                        <span className="question-company">{q.company_name}</span>
                        <span 
                          className="question-difficulty" 
                          style={{ color: getDifficultyColor(q.difficulty) }}
                        >
                          {q.difficulty}
                        </span>
                        <span className="question-duration">⏱ 5 min</span>
                      </div>
                    </div>
                    
                    <div className="question-content">
                      <h3 className="question-text">{q.question}</h3>
                    </div>

                    <div className="question-actions">
                      <button
                        className="practice-button"
                        onClick={() => navigate(`/jobs?startPractice=${q.id}&jobId=${q.source_id}`)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="practice-button-icon">
                          <path d="M3 12H5M8 6V18M12 3V21M16 6V18M19 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Start Practice
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;


